export const ATTACK_SETUP_STORAGE_KEY = 'attackSetupConfig'

export const ATTACK_TEMPLATE_FIELDS = Object.freeze([
    {
        id: 'pointsOuters',
        label: 'Points Outers Attack Template',
        pluginKey: 'attackbarronsouterrealms',
        helper: 'Attack template for Points Outers module (angriff.txt)',
        placeholder: 'Attack template for Points Outers module (angriff.txt)'
    },
    {
        id: 'camp',
        label: 'Camp Attack Template',
        pluginKey: 'attack',
        helper: 'Attack template for camp attacks (attack_Camp.txt)',
        placeholder: 'Attack template for camp attacks (attack_Camp.txt)'
    },
    {
        id: 'khan',
        label: 'Khan Attack Template',
        pluginKey: 'attackkhan',
        helper: 'Attack template for Khan camp attacks (attack_Khan.txt)',
        placeholder: 'Attack template for Khan camp attacks (attack_Khan.txt)'
    },
    {
        id: 'berrimondWorld',
        label: 'Berrimond World Attack Template',
        pluginKey: 'berikingdom',
        helper: 'Attack template for Berrimond World attacks (attack_berri.txt)',
        placeholder: 'Attack template for Berrimond World attacks (attack_berri.txt)'
    },
    {
        id: 'berriGreen',
        label: 'Berri Green Attack Template',
        pluginKey: 'attackberi',
        helper: 'Attack template for Berri Green attacks (attack_BerriGreen.txt)',
        placeholder: 'Attack template for Berri Green attacks (attack_BerriGreen.txt)'
    },
    {
        id: 'fortressFire',
        label: 'Fortress Fire Template',
        pluginKey: 'attackfortresses',
        helper: 'Attack template for Fortress Fire attacks',
        placeholder: 'Attack template for Fortress Fire attacks'
    },
    {
        id: 'fortressIce',
        label: 'Fortress Ice Template',
        pluginKey: 'attackfortresses_glacier',
        helper: 'Attack template for Fortress Ice attacks',
        placeholder: 'Attack template for Fortress Ice attacks'
    },
    {
        id: 'fortressSand',
        label: 'Fortress Sand Template',
        pluginKey: 'attackfortresses_sand',
        helper: 'Attack template for Fortress Sand attacks',
        placeholder: 'Attack template for Fortress Sand attacks'
    }
])

export const EMPTY_ATTACK_TEMPLATE_REFERENCE = Object.freeze({
    templateId: '',
    name: '',
    ownerUsername: '',
    description: '',
    template: null
})

const cloneValue = value => {
    if (typeof structuredClone === 'function')
        return structuredClone(value)

    return JSON.parse(JSON.stringify(value ?? {}))
}

export const normalizeAttackTemplateReference = value => ({
    templateId: [undefined, null, ''].includes(value?.templateId) ? '' : String(value.templateId),
    name: String(value?.name ?? ''),
    ownerUsername: String(value?.ownerUsername ?? ''),
    description: String(value?.description ?? ''),
    template: value?.template && typeof value.template === 'object' ? cloneValue(value.template) : null
})

export const normalizeAttackSetupConfig = value => {
    let rawValue = value

    if (typeof rawValue === 'string') {
        try {
            rawValue = JSON.parse(rawValue)
        }
        catch {
            rawValue = {}
        }
    }

    rawValue = rawValue && typeof rawValue === 'object' ? rawValue : {}
    const rawAssignments = rawValue.assignments && typeof rawValue.assignments === 'object' ? rawValue.assignments : {}

    return {
        correctAttackSetups: rawValue.correctAttackSetups !== false,
        assignments: ATTACK_TEMPLATE_FIELDS.reduce((result, field) => {
            const rawAssignment = rawAssignments[field.id]
            result[field.id] = rawAssignment && typeof rawAssignment === 'object' ? {
                templateId: [undefined, null, ''].includes(rawAssignment.templateId) ? '' : String(rawAssignment.templateId),
                name: String(rawAssignment.name ?? ''),
                ownerUsername: String(rawAssignment.ownerUsername ?? ''),
                description: String(rawAssignment.description ?? ''),
                pluginKey: String(rawAssignment.pluginKey ?? field.pluginKey)
            } : {
                templateId: '',
                name: '',
                ownerUsername: '',
                description: '',
                pluginKey: field.pluginKey
            }
            return result
        }, {})
    }
}

export const getAttackTemplateFieldById = fieldId =>
    ATTACK_TEMPLATE_FIELDS.find(field => field.id === fieldId) ?? null

export const getAttackSetupAssignment = (userPlugins, fieldId) =>
    normalizeAttackSetupConfig(userPlugins?.[ATTACK_SETUP_STORAGE_KEY]).assignments[fieldId] ?? null

export const getSavedAttackTemplateReference = (pluginState, fallbackAssignment) =>
    normalizeAttackTemplateReference({
        templateId:
            pluginState?.savedAttackTemplateId ||
            fallbackAssignment?.templateId,
        name:
            pluginState?.savedAttackTemplateName ??
            fallbackAssignment?.name,
        ownerUsername:
            pluginState?.savedAttackTemplateOwner ??
            fallbackAssignment?.ownerUsername,
        description: fallbackAssignment?.description,
        template: pluginState?.attackTemplateBuilder
    })

export const syncAttackTemplateSelection = (nextPlugins, fieldId, template) => {
    const field = getAttackTemplateFieldById(fieldId)
    if (!field)
        return nextPlugins

    const currentConfig = normalizeAttackSetupConfig(nextPlugins?.[ATTACK_SETUP_STORAGE_KEY])
    const normalizedTemplate = normalizeAttackTemplateReference({
        templateId: template?.id ?? template?.templateId,
        name: template?.name,
        ownerUsername: template?.ownerUsername,
        description: template?.description,
        template: template?.template
    })

    currentConfig.assignments[field.id] = {
        templateId: normalizedTemplate.templateId,
        name: normalizedTemplate.name,
        ownerUsername: normalizedTemplate.ownerUsername,
        description: normalizedTemplate.description,
        pluginKey: field.pluginKey
    }

    nextPlugins[ATTACK_SETUP_STORAGE_KEY] = currentConfig
    nextPlugins[field.pluginKey] ??= {}
    nextPlugins[field.pluginKey].savedAttackTemplateId = normalizedTemplate.templateId ? Number(normalizedTemplate.templateId) : 0
    nextPlugins[field.pluginKey].savedAttackTemplateName = normalizedTemplate.name
    nextPlugins[field.pluginKey].savedAttackTemplateOwner = normalizedTemplate.ownerUsername
    nextPlugins[field.pluginKey].attackTemplateBuilder = normalizedTemplate.template ? cloneValue(normalizedTemplate.template) : {}

    if (field.id === 'camp') {
        const currentCampSettings =
            nextPlugins[field.pluginKey].campBotSettings && typeof nextPlugins[field.pluginKey].campBotSettings === 'object' ?
                nextPlugins[field.pluginKey].campBotSettings :
                {}

        nextPlugins[field.pluginKey].campBotSettings = {
            ...currentCampSettings,
            attackTemplate: normalizedTemplate
        }
    }

    return nextPlugins
}
